import {mat4} from "gl-matrix";
import * as WebGLUtils from "%COMMON/WebGLUtils";
import {ShaderLocationsVault} from "%COMMON/ShaderLocationsVault";
import {Renderer} from './Renderer';
import {generateDNAModel} from "./DNA";
import {Model, CameraMap, Camera, CameraProperties, CameraAnimationState} from "./types";
import {read, writeToFile} from './ModelIO';
import {generateQuadcopterModel} from './Quadcopter';
import {generateSceneModel, generateTestSceneModel} from './Scene';
import {generateCameraMap} from './Camera';


/**
 * This class encapsulates the "view", where all of our WebGL code resides.
 */
export class View {
    // Models
    private dnaModel: Model;
    private readonly quadcopterModel: Model;
    private sceneModel: Model;
    private readonly testSceneModel: Model;
    //the webgl rendering context. All WebGL functions will be called on this object
    private readonly gl: WebGLRenderingContext;
    //an object that represents a WebGL shader
    private shaderProgram: WebGLProgram;
    private readonly proj: mat4;
    private modelview: mat4;
    private shaderLocations: ShaderLocationsVault;
    private readonly cameraMap: CameraMap;
    private selectedCamera: Camera;
    private renderer: Renderer;
    private readonly cameraState: CameraAnimationState;
    private time: number;

    constructor(gl: WebGLRenderingContext) {
        this.gl = gl;
        this.cameraState = {orbit: 0, orbit_v: 0, orbit_a: 0}

        //set the clear color
        this.gl.clearColor(1, 1, 1, 1);
        this.proj = mat4.ortho(mat4.create(), -800, 800, -800, 800, 0.1, 10000);
        // get the width and height of the canvas element
        const {width, height} = document.getElementById('glCanvas').getBoundingClientRect()
        this.gl.viewport(0, 0, width, height);
        this.dnaModel = generateDNAModel();
        this.quadcopterModel = generateQuadcopterModel();
        this.sceneModel = generateSceneModel();
        this.testSceneModel = generateTestSceneModel();
        this.cameraMap = generateCameraMap(this.cameraState);
        this.selectedCamera = Camera.ORBIT;
        this.time = 0;
    }


    /**
     * Initialize the shaders and build the shader program
     * @param vShaderSource
     * @param fShaderSource
     */
    public initShaders(vShaderSource: string, fShaderSource: string) {
        //create and set up the shader
        // init lights and get the number of them
        this.renderer = new Renderer(this.gl, this.proj);
        const numLights = this.renderer.lightPass(this.cameraMap.get(this.selectedCamera).mat, this.sceneModel, 0, true);
        const shaderF = fShaderSource.split('numLights').join(`${numLights}`);
        console.log(shaderF);
        this.shaderProgram = WebGLUtils.createShaderProgram(this.gl, vShaderSource, shaderF);
        //get all the shader variable locations
        this.shaderLocations = new ShaderLocationsVault(this.gl, this.shaderProgram);
        this.renderer.setShaderLocations(this.shaderLocations);
        //enable the current program
        this.gl.useProgram(this.shaderProgram);

    }

    public setModelFromFile = (e: Event): void => {
        const tgt = e.target as HTMLInputElement
        const file = tgt.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => this.sceneModel = read(reader.result as string);
        reader.readAsText(file);
    }

    public downloadModel = (e: Event): void => {
        writeToFile(this.sceneModel);
    }


    public animate(): void {
        this.time += 1;

        const cam: CameraProperties = this.cameraMap.get(this.selectedCamera);
        cam.animFunc(cam.mat, this.time);

        this.draw();
    }

    public draw(): void {
        //clear the window
        this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT);
        this.gl.enable(this.gl.DEPTH_TEST);

        const cam: mat4 = this.cameraMap.get(this.selectedCamera).mat;
        this.modelview = mat4.clone(cam);
        this.gl.useProgram(this.shaderProgram);

        //draw the model
        this.renderer.lightPass(this.modelview, this.sceneModel, this.time);
        this.renderer.render(this.modelview, this.sceneModel, this.time);
    }

    /**
     * Event listener which handles changing the acceleration values for the camera.
     */
    public handleKeyDown(code: string) {
        if (code === 'ArrowRight') {
            this.cameraState.orbit_a = 0.001;
        } else if (code === 'ArrowLeft') {
            this.cameraState.orbit_a = -0.001;
        } else if (code === 'ArrowUp') {
            // this.shift_up += 0.1;
        } else if (code === 'ArrowDown') {
            // this.shift_up -= 0.1;
        } else if (code === 'Digit1') {
            this.selectedCamera = Camera.ORBIT;
        } else if (code === 'Digit2') {
            this.selectedCamera = Camera.SURVEY;
        } else if (code === 'Digit3') {
            this.selectedCamera = Camera.QUADCOPTER;
        }
    }

    /**
     * Event listener which will set the acceleration to zero when the key is lifted
     */
    public handleKeyUp() {
        this.cameraState.orbit_a = 0;
    };


}
